/**
 * Single source of truth for kiosk device settings:
 *   - storage keys
 *   - compiled-in defaults
 *   - reader helpers that resolve "what is the kiosk *actually* configured to do
 *     right now" (storage value if set, else default)
 *
 * The fleet heartbeat (POST /api/kiosk/fleet-heartbeat → hex-monitor) uses the
 * snapshot returned by readEffectiveKioskSettings() so the fleet dashboard
 * reflects the kiosk's runtime configuration, including defaults — not just
 * whatever happens to be present in localStorage.
 *
 * When adding a new setting, define its DEFAULT_* here and route every
 * runtime consumer through the reader helper so the heartbeat and the kiosk
 * agree.
 */

import { getKioskIdentity } from "./kiosk-identity";
import { retrieveAllParams } from "./param-storage";

// ─── Storage keys ───────────────────────────────────────────────────────────

export const NAVIGATION_MODE_KEY = "kiosk_navigation_mode";
export const PERMISSION_REQUEST_MODE_KEY = "kiosk_permission_request_mode";
// SettingsTab writes JSON-stringified milliseconds here.
export const INACTIVITY_TIMEOUT_KEY = "kiosk_settings_inactivityTimeout";
// Mirrored raw string written by SettingsTab so the WKWebView-reliable
// inactivity loader in kiosk-renderer can read without JSON.parse.
export const INACTIVITY_TIMEOUT_LEGACY_KEY = "kiosk_inactivity_timeout";

// ─── Defaults ───────────────────────────────────────────────────────────────

export const DEFAULT_KIOSK_NAVIGATION_MODE = "stateMachine" as const;
export const DEFAULT_KIOSK_PERMISSION_REQUEST_MODE = "startup" as const;
export const DEFAULT_KIOSK_INACTIVITY_TIMEOUT_MS = 60_000;
export const DEFAULT_KIOSK_QR_MODE = false;

export type KioskNavigationMode = "stateMachine" | "window";
export type KioskPermissionRequestMode = "startup" | "onDemand";

export interface KioskSettingsSnapshot {
  kioskName: string | null;
  kioskLocation: string | null;
  practiceId: string | null;
  ehrLocationId: string | null;
  qrMode: boolean;
  inactivityTimeoutMs: number;
  navigationMode: KioskNavigationMode;
  permissionRequestMode: KioskPermissionRequestMode;
}

// ─── Reader helpers ─────────────────────────────────────────────────────────

export function readKioskNavigationMode(): KioskNavigationMode {
  if (typeof window === "undefined") return DEFAULT_KIOSK_NAVIGATION_MODE;
  return localStorage.getItem(NAVIGATION_MODE_KEY) === "window"
    ? "window"
    : DEFAULT_KIOSK_NAVIGATION_MODE;
}

export function readKioskPermissionRequestMode(): KioskPermissionRequestMode {
  if (typeof window === "undefined") {
    return DEFAULT_KIOSK_PERMISSION_REQUEST_MODE;
  }
  return localStorage.getItem(PERMISSION_REQUEST_MODE_KEY) === "onDemand"
    ? "onDemand"
    : DEFAULT_KIOSK_PERMISSION_REQUEST_MODE;
}

export function readKioskInactivityTimeoutMs(): number {
  if (typeof window === "undefined") return DEFAULT_KIOSK_INACTIVITY_TIMEOUT_MS;

  const settingsRaw = localStorage.getItem(INACTIVITY_TIMEOUT_KEY);
  if (settingsRaw) {
    try {
      const parsed = Number(JSON.parse(settingsRaw));
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    } catch {
      // fall through to legacy key
    }
  }

  const legacyRaw = localStorage.getItem(INACTIVITY_TIMEOUT_LEGACY_KEY);
  if (legacyRaw) {
    const parsed = Number.parseInt(legacyRaw, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return DEFAULT_KIOSK_INACTIVITY_TIMEOUT_MS;
}

/**
 * Snapshot of effective kiosk settings (storage + URL params + compiled-in
 * defaults). Identity (kioskName, kioskLocation) and launch params
 * (practiceId, ehrLocationId) have no compiled-in fallback and remain null
 * when unset; everything else resolves to its runtime default.
 */
export function readEffectiveKioskSettings(): KioskSettingsSnapshot {
  if (typeof window === "undefined") {
    return {
      kioskName: null,
      kioskLocation: null,
      practiceId: null,
      ehrLocationId: null,
      qrMode: DEFAULT_KIOSK_QR_MODE,
      inactivityTimeoutMs: DEFAULT_KIOSK_INACTIVITY_TIMEOUT_MS,
      navigationMode: DEFAULT_KIOSK_NAVIGATION_MODE,
      permissionRequestMode: DEFAULT_KIOSK_PERMISSION_REQUEST_MODE,
    };
  }

  const identity = getKioskIdentity();
  const mc = retrieveAllParams();
  return {
    kioskName: identity.kioskName,
    kioskLocation: identity.kioskLocation,
    practiceId: mc.practiceId ?? null,
    ehrLocationId: mc.ehrLocationId ?? null,
    qrMode: mc.qrMode ?? DEFAULT_KIOSK_QR_MODE,
    inactivityTimeoutMs: readKioskInactivityTimeoutMs(),
    navigationMode: readKioskNavigationMode(),
    permissionRequestMode: readKioskPermissionRequestMode(),
  };
}
